'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight, CheckCircle } from 'lucide-react'
import { Wordmark } from '@/components/brand'
import { createClient } from '@/lib/supabase/client'
import { sanitizeNextPath } from '@/lib/auth/redirect'

type PromptMomentNotification = {
  isNotDisplayed?: () => boolean
  isSkippedMoment?: () => boolean
  getNotDisplayedReason?: () => string
  getSkippedReason?: () => string
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (options: {
            client_id: string
            callback: () => void
            cancel_on_tap_outside?: boolean
            auto_select?: boolean
            itp_support?: boolean
          }) => void
          prompt: (listener?: (notification: PromptMomentNotification) => void) => void
        }
      }
    }
  }
}

type LoginFormClientProps = {
  enableOneTap?: boolean
}

let googleScriptPromise: Promise<void> | null = null

function ensureGoogleScriptLoaded(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve()
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve()
  }

  if (googleScriptPromise) {
    return googleScriptPromise
  }

  googleScriptPromise = new Promise<void>((resolve, reject) => {
    const scriptId = 'google-gsi-client'
    let script = document.getElementById(scriptId) as HTMLScriptElement | null

    const waitForGoogle = () => {
      const maxAttempts = 50
      let attempts = 0
      const timer = window.setInterval(() => {
        attempts += 1
        if (window.google?.accounts?.id) {
          window.clearInterval(timer)
          resolve()
          return
        }

        if (attempts >= maxAttempts) {
          window.clearInterval(timer)
          reject(new Error('Google GSI SDK loaded but google.accounts.id is unavailable'))
        }
      }, 100)
    }

    if (!script) {
      script = document.createElement('script')
      script.id = scriptId
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onerror = () => reject(new Error('Failed to load Google GSI SDK script'))
      script.onload = waitForGoogle
      document.head.appendChild(script)
      return
    }

    waitForGoogle()
  }).catch((error) => {
    googleScriptPromise = null
    throw error
  })

  return googleScriptPromise ?? Promise.resolve()
}

export default function LoginFormClient({ enableOneTap = false }: LoginFormClientProps) {
  const [emailOpen, setEmailOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const oneTapInitializedRef = useRef(false)
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const supabase = createClient()
  const safeNextPath = typeof window === 'undefined'
    ? '/'
    : sanitizeNextPath(new URLSearchParams(window.location.search).get('next'))
  const callbackRedirectTo = typeof window === 'undefined'
    ? '/auth/callback'
    : `${window.location.origin}/auth/callback?next=${encodeURIComponent(safeNextPath)}`

  const handleGoogle = useCallback(async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: callbackRedirectTo },
    })
  }, [callbackRedirectTo, supabase.auth])

  useEffect(() => {
    if (!enableOneTap) {
      return
    }

    const clientId = process.env.NEXT_PUBLIC_GOOGLE_GSI_CLIENT_ID
    if (!clientId || oneTapInitializedRef.current) {
      return
    }

    let isCancelled = false

    const initializeOneTap = async () => {
      try {
        await ensureGoogleScriptLoaded()
        if (isCancelled || oneTapInitializedRef.current || !window.google?.accounts?.id) {
          return
        }

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: () => {
            handleGoogle().catch(() => {
              console.warn('Google OAuth fallback button required after One Tap callback.')
            })
          },
          cancel_on_tap_outside: false,
          auto_select: false,
          itp_support: true,
        })

        window.google.accounts.id.prompt((notification) => {
          if (process.env.NODE_ENV !== 'development') {
            return
          }

          const isNotDisplayed = notification.isNotDisplayed?.() ?? false
          const isSkippedMoment = notification.isSkippedMoment?.() ?? false
          const notDisplayedReason = notification.getNotDisplayedReason?.()
          const skippedReason = notification.getSkippedReason?.()

          if (isNotDisplayed || isSkippedMoment) {
            console.info('[OneTap Debug]', {
              isNotDisplayed,
              isSkippedMoment,
              notDisplayedReason,
              skippedReason,
            })
          }
        })

        oneTapInitializedRef.current = true
      } catch {
        console.warn('Google One Tap unavailable; fallback button remains active.')
      }
    }

    initializeOneTap()

    return () => {
      isCancelled = true
    }
  }, [enableOneTap, handleGoogle])

  const handleEmailSubmit = async () => {
    if (!isValidEmail || loading) return
    setLoading(true)
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: callbackRedirectTo },
    })
    setLoading(false)
    if (!error) setSent(true)
  }

  if (sent) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--bg-base)] px-4">
        <div className="w-full max-w-[360px] flex flex-col items-center gap-3 text-center">
          <CheckCircle size={20} color="var(--success)" />
          <p className="text-[14px] font-semibold text-[var(--text-primary)]">
            Check your inbox
          </p>
          <p className="text-[13px] text-[var(--text-secondary)]">
            We sent a link to{' '}
            <span className="text-[var(--accent)] font-medium">{email}</span>
          </p>
          <div className="flex gap-2 mt-1">
            <a href="https://mail.google.com" target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 rounded-[9999px] text-[12px] border border-[var(--border-default)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-colors duration-[time:var(--t-fast)]">
              Open Gmail
            </a>
            <a href="https://outlook.live.com" target="_blank" rel="noopener noreferrer"
              className="px-4 py-2 rounded-[9999px] text-[12px] border border-[var(--border-default)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-colors duration-[time:var(--t-fast)]">
              Open Outlook
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--bg-base)] px-4">
      <div className="w-full max-w-[360px] flex flex-col items-center gap-3">

        {/* WORDMARK — canonical component (Fraunces italic 700) */}
        <Wordmark size={15} className="mb-2" />

        {/* TÍTULO */}
        <h1 className="text-[22px] font-extrabold tracking-tight text-[var(--text-primary)] text-center mb-2">
          See you inside
        </h1>

        {/* GOOGLE */}
        <button
          onClick={handleGoogle}
          className="w-full flex items-center justify-center gap-2 bg-white hover:bg-[#f5f5f5] text-[#0a0a0a] text-[13px] font-medium rounded-[9999px] py-3 border border-[rgba(0,0,0,0.08)] transition-colors duration-[time:var(--t-fast)]"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          Continue with Google
        </button>

        {/* EMAIL TOGGLE */}
        <button
          onClick={() => setEmailOpen(!emailOpen)}
          className="w-full flex items-center justify-center bg-transparent hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-[13px] font-medium rounded-[9999px] py-2.5 border border-[var(--border-default)] hover:border-[var(--border-strong)] transition-colors duration-[time:var(--t-fast)]"
        >
          {emailOpen ? 'Use Google instead' : 'Continue with email'}
        </button>

        {/* EMAIL SECTION */}
        <AnimatePresence>
          {emailOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="w-full flex flex-col gap-2 overflow-hidden"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && isValidEmail && handleEmailSubmit()}
                className="w-full rounded-[9999px] bg-[var(--bg-input)] border border-[var(--border-default)] focus:border-[var(--border-focus)] focus:outline-none px-4 py-3 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] transition-colors duration-[time:var(--t-fast)]"
              />
              <AnimatePresence>
                {isValidEmail && (
                  <motion.button
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.14 }}
                    onClick={handleEmailSubmit}
                    disabled={loading}
                    className="w-full rounded-[9999px] py-3 bg-white hover:bg-white/90 text-black text-[13px] font-medium flex items-center justify-center gap-2 transition-colors duration-[time:var(--t-fast)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading
                      ? 'Sending...'
                      : <><span>Go</span><ArrowRight size={14} strokeWidth={1.5} /></>
                    }
                  </motion.button>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {/* FOOTER */}
      </div>

      <p className="fixed bottom-5 left-0 right-0 text-[11px] text-[var(--text-muted)] text-center">
        <a href="/terms" className="hover:text-[var(--text-secondary)] transition-colors duration-[time:var(--t-fast)]">Terms</a>
        {' · '}
        <a href="/privacy" className="hover:text-[var(--text-secondary)] transition-colors duration-[time:var(--t-fast)]">Privacy</a>
      </p>
    </div>
  )
}
